const SUPPORT_REQUESTS_STORAGE_KEY = 'asi:support-requests';
const SUPPORT_REQUESTS_EVENT = 'asi:support-requests-changed';

function isBrowser() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function readRequests() {
  if (!isBrowser()) {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(SUPPORT_REQUESTS_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function persistRequests(requests) {
  if (!isBrowser()) {
    return;
  }
  const payload = Array.isArray(requests) ? requests : [];
  window.localStorage.setItem(SUPPORT_REQUESTS_STORAGE_KEY, JSON.stringify(payload));
  if (typeof window.CustomEvent === 'function') {
    window.dispatchEvent(new CustomEvent(SUPPORT_REQUESTS_EVENT, { detail: { requests: payload } }));
  } else {
    const event = document.createEvent('Event');
    event.initEvent(SUPPORT_REQUESTS_EVENT, false, false);
    event.detail = { requests: payload };
    window.dispatchEvent(event);
  }
}

function generateId() {
  return `sr_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function getAllSupportRequests() {
  return readRequests();
}

export function createSupportRequest({ name, email, subject, message }) {
  if (!name || !email || !subject || !message) {
    throw new Error('All fields are required.');
  }
  const trimmedName = name.trim();
  const trimmedEmail = email.trim();
  const trimmedSubject = subject.trim();
  const trimmedMessage = message.trim();
  if (!trimmedName || !trimmedEmail || !trimmedSubject || !trimmedMessage) {
    throw new Error('All fields are required.');
  }
  const newRequest = {
    id: generateId(),
    name: trimmedName,
    email: trimmedEmail,
    subject: trimmedSubject,
    message: trimmedMessage,
    status: 'open',
    createdAt: new Date().toISOString(),
    response: null,
    respondedAt: null,
    dismissedAt: null,
    transcript: [],
  };
  const requests = readRequests();
  requests.unshift(newRequest);
  persistRequests(requests);
  return newRequest;
}

export function respondToSupportRequest(id, responseMessage) {
  if (!id) {
    return null;
  }
  const requests = readRequests();
  const index = requests.findIndex((item) => item.id === id);
  if (index === -1) {
    return null;
  }
  const trimmedResponse = (responseMessage || '').trim();
  const now = new Date().toISOString();
  const updated = {
    ...requests[index],
    response: trimmedResponse || null,
    status: trimmedResponse ? 'responded' : 'open',
    respondedAt: trimmedResponse ? now : null,
    dismissedAt: null,
    transcript: [
      ...(Array.isArray(requests[index].transcript) ? requests[index].transcript : []),
      {
        from: 'user',
        body: requests[index].message,
        at: requests[index].createdAt,
      },
      ...(trimmedResponse
        ? [
            {
              from: 'admin',
              body: trimmedResponse,
              at: now,
            },
          ]
        : []),
    ],
  };
  requests[index] = updated;
  persistRequests(requests);
  return updated;
}

export function subscribeToSupportRequestChanges(callback) {
  if (!isBrowser() || typeof callback !== 'function') {
    return () => {};
  }
  const handler = (event) => {
    const detail = event && event.detail && event.detail.requests;
    if (detail) {
      callback(detail);
    } else {
      callback(readRequests());
    }
  };
  window.addEventListener(SUPPORT_REQUESTS_EVENT, handler);
  return () => window.removeEventListener(SUPPORT_REQUESTS_EVENT, handler);
}

export function dismissSupportResponse(id) {
  if (!id) {
    return null;
  }
  const requests = readRequests();
  const index = requests.findIndex((item) => item.id === id);
  if (index === -1) {
    return null;
  }
  const updated = {
    ...requests[index],
    dismissedAt: new Date().toISOString(),
  };
  requests[index] = updated;
  persistRequests(requests);
  return updated;
}

export default {
  getAllSupportRequests,
  createSupportRequest,
  respondToSupportRequest,
  subscribeToSupportRequestChanges,
  dismissSupportResponse,
};
